import { useState } from 'react'
import QRCode from 'qrcode'
import { AidStation, Segment, CalibrationProfile } from '../../types'
import { CrewCalculations } from '../../lib/crewCalculations'
import { CrewStorage } from '../../lib/crewStorage'
import { useCrewCheckIns } from '../../hooks/useCrewCheckIns'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Progress } from '../ui/progress'
import { Separator } from '../ui/separator'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Alert, AlertDescription } from '../ui/alert'
import {
  Share2,
  Download,
  Copy,
  AlertCircle,
  CheckCircle2,
  Clock,
  MapPin,
  TrendingUp,
  TrendingDown,
  Target
} from 'lucide-react'

interface CrewSummaryProps {
  aidStations: AidStation[]
  segments: Segment[]
  calibrationProfile?: CalibrationProfile | null
  slug: string
  runTitle: string
  startTime?: Date
  className?: string
}

export function CrewSummary({
  aidStations,
  segments,
  calibrationProfile,
  slug,
  runTitle,
  startTime,
  className = ''
}: CrewSummaryProps) {
  const [showShareDialog, setShowShareDialog] = useState(false)
  const [showImportDialog, setShowImportDialog] = useState(false)
  const [importUrl, setImportUrl] = useState('')
  const [importError, setImportError] = useState<string | null>(null)
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null)

  // Ensure finish line aid station exists
  const stationsWithFinish = CrewCalculations.ensureFinishLineStation(aidStations, segments)
  
  // Use the crew check-ins hook to get the current state
  const { checkIns } = useCrewCheckIns(slug, stationsWithFinish, startTime)

  // Calculate summary statistics using stations with finish line
  const progress = CrewCalculations.getRaceProgress(stationsWithFinish, segments, checkIns)
  const nextStation = CrewCalculations.getNextAidStation(stationsWithFinish, checkIns)
  const totalStations = stationsWithFinish.length
  const completedStations = checkIns.length

  // Calculate overall pace status
  let overallStatus: 'on-pace' | 'ahead' | 'behind' = 'on-pace'
  if (checkIns.length > 0) {
    const latestCheckIn = checkIns.sort((a, b) => b.actualTimeMinutes - a.actualTimeMinutes)[0]
    const station = stationsWithFinish.find(s => s.id === latestCheckIn.aidStationId)
    if (station) {
      const calc = CrewCalculations.calculateAidStationETA(
        station,
        segments,
        calibrationProfile,
        checkIns,
        startTime,
        stationsWithFinish
      )
      overallStatus = calc.status
    }
  }

  const generateQRCode = async (url: string) => {
    try {
      const dataUrl = await QRCode.toDataURL(url, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      })
      setQrCodeDataUrl(dataUrl)
    } catch (error) {
      console.error('Error generating QR code:', error)
    }
  }

  const handleShare = async () => {
    const shareUrl = CrewStorage.getShareableUrl(slug)
    
    if (navigator.share && navigator.canShare({ url: shareUrl })) {
      try {
        await navigator.share({
          title: `${runTitle} - Crew Updates`,
          text: 'Live crew updates for this ultra running race',
          url: shareUrl
        })
        return
      } catch (error) {
        console.log('Native sharing failed, falling back to dialog')
      }
    }
    
    await generateQRCode(shareUrl)
    setShowShareDialog(true)
  }

  const handleCopyUrl = async () => {
    const shareUrl = CrewStorage.getShareableUrl(slug)
    await navigator.clipboard.writeText(shareUrl)
    // Could add toast notification here
  }

  const handleImport = async () => {
    setImportError(null)
    
    if (!importUrl.trim()) {
      setImportError('Please enter a URL')
      return
    }

    const success = CrewStorage.importFromUrl(slug, importUrl)
    if (success) {
      setShowImportDialog(false)
      setImportUrl('')
      // Trigger a refresh of the parent component
      window.location.reload()
    } else {
      setImportError('No valid crew data found in the URL')
    }
  }

  const getStatusInfo = () => {
    switch (overallStatus) {
      case 'ahead':
        return {
          icon: <TrendingUp className="h-5 w-5 text-green-600" />,
          label: 'Ahead of Pace',
          color: 'text-green-600'
        }
      case 'behind':
        return {
          icon: <TrendingDown className="h-5 w-5 text-red-600" />,
          label: 'Behind Pace',
          color: 'text-red-600'
        }
      case 'on-pace':
      default:
        return {
          icon: <Target className="h-5 w-5 text-blue-600" />,
          label: 'On Pace',
          color: 'text-blue-600'
        }
    }
  }

  const statusInfo = getStatusInfo()

  return (
    <>
      <Card className={`${className} print:hidden`}>
        <CardHeader>
          <div className="flex justify-between items-start">
            <CardTitle className="flex items-center space-x-2">
              <span>Crew Summary</span>
              {statusInfo.icon}
            </CardTitle>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowImportDialog(true)}
                className="py-2 px-3 text-sm font-medium touch-manipulation"
              >
                <Download className="h-4 w-4 mr-1" />
                Import
              </Button>
              <Button
                size="sm"
                onClick={handleShare}
                className="py-2 px-3 text-sm font-medium touch-manipulation"
              >
                <Share2 className="h-4 w-4 mr-1" />
                Share
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Overall Status */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Overall Status</p>
              <p className={`text-lg font-semibold ${statusInfo.color}`}>
                {statusInfo.label}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-gray-600">Last Update</p>
              <p className="text-sm text-gray-900">
                {progress.lastCheckInTime 
                  ? progress.lastCheckInTime.toLocaleTimeString()
                  : 'No check-ins yet'
                }
              </p>
            </div>
          </div>

          <Separator />

          {/* Progress Statistics */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <MapPin className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium">Distance Progress</span>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span>{progress.completedDistance.toFixed(1)} km</span>
                  <span>{progress.totalDistance.toFixed(1)} km</span>
                </div>
                <Progress value={progress.progressPercent} className="h-2" />
                <p className="text-xs text-gray-500 text-center">
                  {progress.progressPercent.toFixed(1)}% complete
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium">Aid Stations</span>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span>{completedStations}</span>
                  <span>{totalStations}</span>
                </div>
                <Progress 
                  value={(completedStations / totalStations) * 100} 
                  className="h-2" 
                />
                <p className="text-xs text-gray-500 text-center">
                  {completedStations} of {totalStations} stations
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Next Station Info or Race Complete */}
          {nextStation ? (
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">Next Expected</span>
              </div>
              <div className="space-y-1">
                <p className="font-semibold text-blue-900">{nextStation.name}</p>
                <p className="text-sm text-blue-700">
                  {nextStation.at_distance_km.toFixed(1)} km
                </p>
                {startTime && (
                  <div className="mt-2">
                    <Badge variant="outline" className="text-blue-700 border-blue-300">
                      ETA: Coming soon
                    </Badge>
                  </div>
                )}
              </div>
            </div>
          ) : completedStations === totalStations && totalStations > 0 ? (
            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-green-900">Race Complete</span>
              </div>
              <div className="space-y-1">
                <p className="font-semibold text-green-900">All aid stations checked in!</p>
                <p className="text-sm text-green-700">
                  🎉 Congratulations on completing the race!
                </p>
              </div>
            </div>
          ) : null}

          {/* Recent Check-ins */}
          {checkIns.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">Recent Check-ins</h4>
              <div className="space-y-2">
                {checkIns
                  .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
                  .slice(0, 3)
                  .map((checkIn) => {
                    const station = stationsWithFinish.find(s => s.id === checkIn.aidStationId)
                    return (
                      <div 
                        key={checkIn.aidStationId} 
                        className="flex justify-between items-center p-2 bg-gray-50 rounded"
                      >
                        <div>
                          <p className="text-sm font-medium">{station?.name}</p>
                          <p className="text-xs text-gray-500">
                            {checkIn.timestamp.toLocaleTimeString()}
                          </p>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          T+ {CrewCalculations.formatElapsedTime(checkIn.actualTimeMinutes)}
                        </Badge>
                      </div>
                    )
                  })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Share Crew Updates</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Share this link with other crew members to sync check-ins across devices.
            </p>
            
            <div className="space-y-3">
              <div className="flex space-x-2">
                <Input
                  value={CrewStorage.getShareableUrl(slug)}
                  readOnly
                  className="text-xs"
                />
                <Button size="sm" onClick={handleCopyUrl} className="py-2 px-3 touch-manipulation">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              
              {qrCodeDataUrl && (
                <div className="text-center">
                  <img 
                    src={qrCodeDataUrl} 
                    alt="QR Code for sharing"
                    className="mx-auto"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Scan with another device to sync check-ins
                  </p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Import Crew Updates</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Import check-ins from another crew member by pasting their share link.
            </p>
            
            <div className="space-y-3">
              <div>
                <Label htmlFor="importUrl">Share URL</Label>
                <Input
                  id="importUrl"
                  value={importUrl}
                  onChange={(e) => setImportUrl(e.target.value)}
                  placeholder="Paste the crew share link here..."
                />
              </div>
              
              {importError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{importError}</AlertDescription>
                </Alert>
              )}
              
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setShowImportDialog(false)}
                >
                  Cancel
                </Button>
                <Button onClick={handleImport}>
                  Import
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}